<template>
    <div ref="ticketListRef" class="parlayInSingleBetSlip">
        <TransitionGroup name="ticketGroupFade">
            <div
                v-for="group in parlayTicketGroups"
                v-bind:key="group.key"
                class="parlayMatchGroup"
                v-bind:class="{ _conflicted: group.isConflicted }"
                v-bind:data-match-group-key="group.key"
            >
                <TransitionGroup name="ticketFade">
                    <div
                        v-for="ticket in group.tickets"
                        v-bind:key="ticket.key"
                        class="parlayTicket"
                        v-bind:class="{ _disabled: !ticket.hasMixParlay }"
                        v-bind:aria-disabled="!ticket.hasMixParlay"
                        v-bind:data-ticket-key="ticket.key"
                        v-on:click="selectTicket(ticket)"
                    >
                        <SingleTicket
                            v-if="ticket.type === TicketType.Single"
                            v-bind:gaClassPrefix="gaClassPrefix"
                            v-bind:delTicketGaSuffix="getDelTicketGaSuffix(ticket)"
                            v-bind:ticket="ticket"
                            v-bind:layout="SingleModeTicketLayout.Parlay"
                            v-bind:isValid="ticket.hasMixParlay"
                            v-bind:isMixParlay="ticket.hasMixParlay"
                            v-bind:parlayOdds="parlayOddsByKey[ticket.key]"
                            v-bind:parlaySubBetResult="parlayDisplaySubBetResults[ticket.key]"
                            v-on:remove="removeTicket(ticket.key)"
                        >
                            <TicketMessage v-if="getTicketMessage(ticket)">
                                {{ getTicketMessage(ticket) }}
                            </TicketMessage>
                        </SingleTicket>
                        <DynamicMarketTicket
                            v-else-if="ticket.type === TicketType.DynamicMarket"
                            v-bind:gaClassPrefix="gaClassPrefix"
                            v-bind:delTicketGaSuffix="getDelTicketGaSuffix(ticket)"
                            v-bind:ticket="ticket"
                            v-bind:layout="SingleModeTicketLayout.Parlay"
                            v-bind:isValid="ticket.hasMixParlay"
                            v-on:remove="removeTicket(ticket.key)"
                        >
                            <TicketMessage v-if="getTicketMessage(ticket)">
                                {{ getTicketMessage(ticket) }}
                            </TicketMessage>
                        </DynamicMarketTicket>
                        <OutrightTicket
                            v-else-if="ticket.type === TicketType.Outright"
                            v-bind:gaClassPrefix="gaClassPrefix"
                            v-bind:delTicketGaSuffix="getDelTicketGaSuffix(ticket)"
                            v-bind:ticket="ticket"
                            v-bind:layout="SingleModeTicketLayout.Parlay"
                            v-bind:isValid="ticket.hasMixParlay"
                            v-on:remove="removeTicket(ticket.key)"
                        >
                            <TicketMessage v-if="getTicketMessage(ticket)">
                                {{ getTicketMessage(ticket) }}
                            </TicketMessage>
                        </OutrightTicket>
                    </div>
                </TransitionGroup>
            </div>
        </TransitionGroup>
        <Transition name="parlayInsufficientFade">
            <div
                v-if="shouldShowParlayInsufficientMessage"
                class="parlayInSingleBetSlip-insufficient"
                data-testid="parlay-ticket-count-insufficient-message"
            >
                <div
                    class="parlayInSingleBetSlip-insufficientMessage"
                    v-bind:class="`${gaClassPrefix}_Btn_AddMoreBet`"
                    v-on:click="emit('addMoreBet')"
                >
                    <ParlayTicketCountInsufficientMessage
                        v-bind:missingCount="missingParlaySubBetCount"
                        v-bind:isTwoSubBetMessage="isTwoSubBetMessage"
                    />
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, ref } from 'vue';
    import ParlayTicketCountInsufficientMessage from '@/components/betSlip/ParlayTicketCountInsufficientMessage.vue';
    import DynamicMarketTicket from '@/components/betSlip/Single/Ticket/DynamicMarketTicket.vue';
    import OutrightTicket from '@/components/betSlip/Single/Ticket/OutrightTicket.vue';
    import SingleTicket from '@/components/betSlip/Single/Ticket/SingleTicket.vue';
    import TicketMessage from '@/components/betSlip/Single/Ticket/TicketMessage.vue';
    import type { Ticket } from '@/interface/ITicket';
    import { SingleModeTicketLayout, TicketType } from '@/interface/enum';
    import {
        formatSingleModeTicketErrorMessage,
        resolveParlayTicketError,
    } from '@/libraries/betSlip/singleModeBetSlipErrorMessage';
    import {
        TWO_SUB_BET_PARLAY_COUNT,
        isParlayEligibleTicket,
    } from '@/libraries/composables/useParlayInSingle';
    import { useScrollTicketListToBottom } from '@/libraries/composables/useScrollTicketListToBottom';
    import { useAccountStore } from '@/stores/accountStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';

    withDefaults(defineProps<{
        gaClassPrefix?: string;
    }>(), {
        gaClassPrefix: '',
    });

    const emit = defineEmits<{
        selectTicket: [key: string];
        addMoreBet: [];
    }>();

    const {
        parlayTicketGroups,
        parlayOddsByKey,
        parlayDisplaySubBetResults,
        parlaySubBetErrors,
        parlaySubBetChangeErrors,
        conflictedParlayKeys,
        minParlaySubBetCount,
        missingParlaySubBetCount,
        shouldShowParlayInsufficientMessage,
    } = storeToRefs(useSingleModeBetSlipStore());
    const { removeTicket } = useSingleModeBetSlipStore();
    const { formatMoney } = useAccountStore();
    const ticketListRef = ref<HTMLElement | null>(null);
    useScrollTicketListToBottom(ticketListRef);

    const isTwoSubBetMessage = computed(() => (
        minParlaySubBetCount.value === TWO_SUB_BET_PARLAY_COUNT
    ));

    function selectTicket(ticket: Ticket) {
        if (!ticket.hasMixParlay) {
            return;
        }
        emit('selectTicket', ticket.key);
    }

    function getDelTicketGaSuffix(ticket: Ticket): string {
        if (conflictedParlayKeys.value.has(ticket.key)) {
            return '_SameMatch';
        }
        if (!ticket.hasMixParlay) {
            return '_NoSupport';
        }
        return '';
    }

    function getTicketMessage(ticket: Ticket): string {
        const error = resolveParlayTicketError({
            isEligible: isParlayEligibleTicket(ticket),
            subBetErrorStatus: parlaySubBetErrors.value[ticket.key],
            openTicketError: parlaySubBetChangeErrors.value[ticket.key]
                ?? ticket.openTicketError,
        });
        return error === null ? '' : formatSingleModeTicketErrorMessage(error, formatMoney);
    }
</script>

<style lang="scss" scoped>
@import "@/scss/components/singleMode/parlayInSingleBetSlip";
</style>
